import { InfoCircleOutlined } from '@ant-design/icons';
import { Form, type FormInstance, Popover, Select, Typography } from 'antd';
import { isBoolean, isEmpty } from 'lodash';
import type React from 'react';
import {
  ANATOMICAL_TERMS_PRESENT_OPTIONS,
  CHROMOSOMALLY_DEFINED_GENDER_OPTIONS,
  CONTAINS_DIALOGUE_OPTIONS,
  EXPLICIT_TERMS_FOUND_OPTIONS,
  FIRST_PERSON_PERSPECTIVE_OPTIONS,
  PHYSICAL_CONTACT_OPTIONS,
  SELF_IDENTIFIED_GENDERS_OPTIONS,
  SETTING_OPTIONS,
} from '../../../../../common/constants';
import CaseInsensitiveSelect, {
  toTitleCase,
} from '../../../../../components/CaseInsensitiveSelect';
import { VoteEnum } from '../../../../../types/common.type';
import { SexuallyInappropriateAnalysesData } from '../SexuallyInappropriateAnalyses';
import {
  anatomicalTermsListPopoverContent,
  anatomicalTermsPresentPopoverContent,
  chromosomallyDefinedGenderPopoverContent,
  containsDialoguePopoverContent,
  explicitTermsFoundPopoverContent,
  explicitTermsListPopoverContent,
  firstPersonPopoverContent,
  physicalContactPopoverContent,
  selfIdentifiedGendersPopoverContent,
  settingPopoverContent,
} from './SignalDetectionFieldsDetails';

const { Text } = Typography;

const infoIcon = (content: React.ReactNode) => (
  <Popover content={content} placement="top" overlayClassName="field-content">
    <InfoCircleOutlined className="signal-detection-info-icon" />
  </Popover>
);

export type SignalDetectionFormValues = {
  vote?: VoteEnum;
  sexuallyClientComments?: string;
  sexuallyFirstPersonPerspective?: boolean | null;
  sexuallyPhysicalContactType?: string[] | null;
  sexuallySetting?: string[] | null;
  sexuallyExplicitTermsFound?: boolean | null;
  sexuallyExplicitTermsList?: string[] | null;
  sexuallyAnatomicalTermsPresent?: boolean | null;
  sexuallyAnatomicalTermsList?: string[] | null;
  sexuallyContainsDialogue?: boolean | null;
  sexuallyChromosomallyDefinedGender?: string[] | null;
  sexuallySelfIdentifiedGenders?: string[] | null;
  sexuallySexualRelationshipType?: string[];
  sexuallyCharacterAge?: string[];
  sexuallyEuphemismsList?: string[];
  sexuallySuggestivePhrasesList?: string[];
  sexuallyPowerImbalancePresent?: boolean | null;
  sexuallyTone?: string[];
  sexuallyExcessiveFocusOnBodyDescription?: boolean | null;
  sexuallyLanguageOfSecrecyOrConcealment?: boolean | null;
  sexuallyRoleplayOrFantasyContext?: boolean | null;
  sexuallyViolenceOverlap?: boolean | null;
  sexuallyNudityDetected?: string[];
  sexuallyNudityExtent?: string[];
  sexuallyNudityType?: string[];
  sexuallySexualActsDetected?: string[];
  sexuallyFinalRating?: number | null;
  sexuallyRatingReason?: string | null;
};

const SignalDetectionForm: React.FC<{
  form: FormInstance;
  setIsSubmitBtnDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  selectSexuallyInappropriateData:
    | SexuallyInappropriateAnalysesData
    | undefined;
}> = ({ form, setIsSubmitBtnDisabled, selectSexuallyInappropriateData }) => {
  const feedbackData = selectSexuallyInappropriateData?.subSection?.feedback;

  return (
    <Form
      layout="vertical"
      form={form}
      className="signal-detection-form-grid"
      onValuesChange={() => {
        setIsSubmitBtnDisabled(false);
      }}
    >
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              First Person Perspective {infoIcon(firstPersonPopoverContent)}
            </span>
          }
          name="sexuallyFirstPersonPerspective"
          className={
            isBoolean(feedbackData?.sexuallyFirstPersonPerspective)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select size="middle" options={FIRST_PERSON_PERSPECTIVE_OPTIONS} />
        </Form.Item>
        {isBoolean(feedbackData?.sexuallyFirstPersonPerspective) &&
          isBoolean(selectSexuallyInappropriateData?.firstPersonPerspective) &&
          !(
            feedbackData?.sexuallyFirstPersonPerspective ===
            selectSexuallyInappropriateData?.firstPersonPerspective
          ) && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {selectSexuallyInappropriateData?.firstPersonPerspective ===
                true
                  ? 'True'
                  : selectSexuallyInappropriateData?.firstPersonPerspective ===
                      false
                    ? 'False'
                    : ''}
              </Text>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Physical Contact Type {infoIcon(physicalContactPopoverContent)}
            </span>
          }
          name="sexuallyPhysicalContactType"
          className={
            !isEmpty(feedbackData?.sexuallyPhysicalContactType)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={PHYSICAL_CONTACT_OPTIONS}
          />
        </Form.Item>

        {!(
          JSON.stringify(
            isEmpty(feedbackData?.sexuallyPhysicalContactType)
              ? []
              : feedbackData?.sexuallyPhysicalContactType,
          ) ===
          JSON.stringify(
            isEmpty(selectSexuallyInappropriateData?.physicalContactType)
              ? []
              : selectSexuallyInappropriateData?.physicalContactType,
          )
        ) && (
          <div className="signal-detection-below-text">
            <Text strong>Originally:</Text>{' '}
            <Text>
              {(selectSexuallyInappropriateData?.physicalContactType || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </Text>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Setting {infoIcon(settingPopoverContent)}
            </span>
          }
          name="sexuallySetting"
          className={
            !isEmpty(feedbackData?.sexuallySetting)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={SETTING_OPTIONS}
            placeholder="Select settings"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallySetting) ===
          JSON.stringify(selectSexuallyInappropriateData?.setting)
        ) && (
          <div className="signal-detection-below-text">
            <Text strong>Originally:</Text>{' '}
            <Text>
              {(selectSexuallyInappropriateData?.setting || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </Text>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Explicit Terms Found {infoIcon(explicitTermsFoundPopoverContent)}
            </span>
          }
          name="sexuallyExplicitTermsFound"
          className={
            isBoolean(feedbackData?.sexuallyExplicitTermsFound)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select size="middle" options={EXPLICIT_TERMS_FOUND_OPTIONS} />
        </Form.Item>
        {isBoolean(feedbackData?.sexuallyExplicitTermsFound) &&
          isBoolean(selectSexuallyInappropriateData?.explicitTermsFound) &&
          !(
            feedbackData?.sexuallyExplicitTermsFound ===
            selectSexuallyInappropriateData?.explicitTermsFound
          ) && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {selectSexuallyInappropriateData?.explicitTermsFound === true
                  ? 'True'
                  : selectSexuallyInappropriateData?.explicitTermsFound ===
                      false
                    ? 'False'
                    : ''}
              </Text>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Explicit terms list {infoIcon(explicitTermsListPopoverContent)}
            </span>
          }
          name="sexuallyExplicitTermsList"
          className={
            !isEmpty(feedbackData?.sexuallyExplicitTermsList)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[]}
            placeholder="Select explicit terms"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallyExplicitTermsList) ===
          JSON.stringify(selectSexuallyInappropriateData?.explicitTermsList)
        ) && (
          <div className="signal-detection-below-text">
            <Text strong>Originally:</Text>{' '}
            <Text>
              {(selectSexuallyInappropriateData?.explicitTermsList || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </Text>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Anatomical Terms Present{' '}
              {infoIcon(anatomicalTermsPresentPopoverContent)}
            </span>
          }
          name="sexuallyAnatomicalTermsPresent"
          className={
            isBoolean(feedbackData?.sexuallyAnatomicalTermsPresent)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select size="middle" options={ANATOMICAL_TERMS_PRESENT_OPTIONS} />
        </Form.Item>
        {isBoolean(feedbackData?.sexuallyAnatomicalTermsPresent) &&
          isBoolean(selectSexuallyInappropriateData?.anatomicalTermsPresent) &&
          !(
            feedbackData?.sexuallyAnatomicalTermsPresent ===
            selectSexuallyInappropriateData?.anatomicalTermsPresent
          ) && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {selectSexuallyInappropriateData?.anatomicalTermsPresent ===
                true
                  ? 'True'
                  : selectSexuallyInappropriateData?.anatomicalTermsPresent ===
                      false
                    ? 'False'
                    : ''}
              </Text>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Anatomical terms list{' '}
              {infoIcon(anatomicalTermsListPopoverContent)}
            </span>
          }
          name="sexuallyAnatomicalTermsList"
          className={
            !isEmpty(feedbackData?.sexuallyAnatomicalTermsList)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={[]}
            placeholder="Select anatomical terms"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallyAnatomicalTermsList) ===
          JSON.stringify(selectSexuallyInappropriateData?.anatomicalTermsList)
        ) && (
          <div className="signal-detection-below-text">
            <Text strong>Originally:</Text>{' '}
            <Text>
              {(selectSexuallyInappropriateData?.anatomicalTermsList || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </Text>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Contains Dialogue {infoIcon(containsDialoguePopoverContent)}
            </span>
          }
          name="sexuallyContainsDialogue"
          className={
            isBoolean(feedbackData?.sexuallyContainsDialogue)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <Select size="middle" options={CONTAINS_DIALOGUE_OPTIONS} />
        </Form.Item>
        {isBoolean(feedbackData?.sexuallyContainsDialogue) &&
          isBoolean(selectSexuallyInappropriateData?.containsDialogue) &&
          !(
            feedbackData?.sexuallyContainsDialogue ===
            selectSexuallyInappropriateData?.containsDialogue
          ) && (
            <div className="signal-detection-below-text">
              <Text strong>Originally:</Text>{' '}
              <Text>
                {selectSexuallyInappropriateData?.containsDialogue === true
                  ? 'True'
                  : selectSexuallyInappropriateData?.containsDialogue === false
                    ? 'False'
                    : ''}
              </Text>
            </div>
          )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Chromosomally Defined Gender{' '}
              {infoIcon(chromosomallyDefinedGenderPopoverContent)}
            </span>
          }
          name="sexuallyChromosomallyDefinedGender"
          className={
            !isEmpty(feedbackData?.sexuallyChromosomallyDefinedGender)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={CHROMOSOMALLY_DEFINED_GENDER_OPTIONS}
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallyChromosomallyDefinedGender) ===
          JSON.stringify(
            selectSexuallyInappropriateData?.chromosomallyDefinedGender,
          )
        ) && (
          <div className="signal-detection-below-text">
            <Text strong>Originally:</Text>{' '}
            <Text>
              {(
                selectSexuallyInappropriateData?.chromosomallyDefinedGender ||
                []
              )
                .map((val: string) => toTitleCase(val))
                .join(', ')}
            </Text>
          </div>
        )}
      </div>
      <div className="signal-detection-form-item signal-detection-form-item">
        <Form.Item
          label={
            <span className="d-flex align-center">
              Self Identified Genders{' '}
              {infoIcon(selfIdentifiedGendersPopoverContent)}
            </span>
          }
          name="sexuallySelfIdentifiedGenders"
          className={
            !isEmpty(feedbackData?.sexuallySelfIdentifiedGenders)
              ? 'signal-detection-form-item--with-below-text'
              : 'signal-detection-form-item'
          }
        >
          <CaseInsensitiveSelect
            mode="tags"
            size="middle"
            options={SELF_IDENTIFIED_GENDERS_OPTIONS}
            placeholder="Select self identified genders"
          />
        </Form.Item>
        {!(
          JSON.stringify(feedbackData?.sexuallySelfIdentifiedGenders) ===
          JSON.stringify(selectSexuallyInappropriateData?.selfIdentifiedGenders)
        ) && (
          <div className="signal-detection-below-text">
            <Text strong>Originally:</Text>{' '}
            <Text>
              {(selectSexuallyInappropriateData?.selfIdentifiedGenders || [])
                .map((val: string) => toTitleCase(val))
                .join(', ') || '--'}
            </Text>
          </div>
        )}
      </div>
    </Form>
  );
};

export default SignalDetectionForm;
